import React, { useState, useEffect, useCallback } from 'react';
import './AdminPanel.css';

const STATUS_LABELS = {
  ativo:        { label: 'Ativo',        cls: 'ap-badge--ativo' },
  inativo:      { label: 'Inativo',      cls: 'ap-badge--inativo' },
  cancelado:    { label: 'Cancelado',    cls: 'ap-badge--cancelado' },
  inadimplente: { label: 'Inadimplente', cls: 'ap-badge--inadimplente' },
};

const DATA_JOBS = [
  { name: 'update-smallcaps-universe', label: 'Universo SMLL' },
  { name: 'backfill-cotahist', label: 'Backfill COTAHIST' },
  { name: 'apply-corporate-actions', label: 'Ações corporativas' },
  { name: 'indicators', label: 'Indicadores' },
  { name: 'backtests', label: 'Backtests' },
  { name: 'all', label: 'Executar sequência completa', emphasis: true },
];

const JOB_LABELS = {
  ...Object.fromEntries(DATA_JOBS.map(job => [job.name, job.label])),
  'alert-monitor': 'Alertas e cotações globais',
  'alert-monitor-silent-hour': 'Alertas — ciclos silenciosos',
  'intraday-scanner': 'Scanner intraday',
  'daily-market-collect': 'Fechamento diário + indicadores',
  'volume-financeiro-diario': 'Volume financeiro diário (COTAHIST)',
  'cdi-diario': 'CDI diário (Banco Central, SGS 12)',
  'options-hourly': 'Cadeias de opções',
  'options-post-close': 'Opções pós-fechamento e volatilidade',
  'risk-daily-report': 'Relatório diário de risco',
  'email-alert-retry': 'Retentativas de e-mail e CDR',
  'whatsapp-alert-dispatch': 'Entrega WhatsApp',
  'job-runs-retention': 'Limpeza do histórico',
};

function fmt(val) {
  if (!val) return '—';
  const d = new Date(val);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function fmtDateTime(val) {
  if (!val) return '—';
  const d = new Date(val);
  if (isNaN(d)) return '—';
  const hoje = new Date();
  const ontem = new Date(hoje); ontem.setDate(hoje.getDate() - 1);
  const mesmaData = (a, b) =>
    a.getDate() === b.getDate() && a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();
  const hora = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  if (mesmaData(d, hoje)) return `Hoje ${hora}`;
  if (mesmaData(d, ontem)) return `Ontem ${hora}`;
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' ' + hora;
}

function fmtBRL(cents) {
  if (!cents) return '—';
  return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export default function AdminPanel({ onExit }) {
  const token = localStorage.getItem('token') || '';
  const [authStatus, setAuthStatus] = useState('checking');
  const [authError, setAuthError] = useState('');

  const [activeTab, setActiveTab] = useState('usuarios');

  // Kiwify clientes
  const [clientes, setClientes] = useState([]);
  const [loadingClientes, setLoadingClientes] = useState(false);
  const [errorClientes, setErrorClientes] = useState('');
  const [search, setSearch]     = useState('');
  const [filterStatus, setFilterStatus] = useState('todos');

  // Platform users
  const [usuarios, setUsuarios]   = useState([]);
  const [loadingUsuarios, setLoadingUsuarios] = useState(false);
  const [errorUsuarios, setErrorUsuarios]   = useState('');
  const [searchUsuarios, setSearchUsuarios] = useState('');
  const [localUsers, setLocalUsers] = useState([]);
  const [legacyPositions, setLegacyPositions] = useState([]);
  const [loadingLegacy, setLoadingLegacy] = useState(false);
  const [errorLegacy, setErrorLegacy] = useState('');
  const [legacyOwners, setLegacyOwners] = useState({});

  const [recoveryFailures, setRecoveryFailures] = useState([]);
  const [loadingRecoveryFailures, setLoadingRecoveryFailures] = useState(false);
  const [errorRecoveryFailures, setErrorRecoveryFailures] = useState('');

  const [copied, setCopied] = useState('');
  const [jobSecret, setJobSecret] = useState('');
  const [jobRunning, setJobRunning] = useState('');
  const [jobResults, setJobResults] = useState({});
  const [activeJob, setActiveJob] = useState(null);
  const [jobOverview, setJobOverview] = useState(null);
  const [jobHistory, setJobHistory] = useState([]);
  const [loadingJobHistory, setLoadingJobHistory] = useState(false);
  const [jobHistoryError, setJobHistoryError] = useState('');

  // WhatsApp Channel
  const [whatsappStatus, setWhatsappStatus] = useState('falta token');
  const [whatsappOrigin, setWhatsappOrigin] = useState(null);
  const [whatsappExpiresAt, setWhatsappExpiresAt] = useState(null);
  const [whatsappInputToken, setWhatsappInputToken] = useState('');
  const [savingWhatsappToken, setSavingWhatsappToken] = useState(false);
  const [testingWhatsapp, setTestingWhatsapp] = useState(false);
  const [whatsappNotice, setWhatsappNotice] = useState('');
  const [whatsappTestResult, setWhatsappTestResult] = useState(null);

  const apiFetch = useCallback(async (path, opts = {}) => {
    const res = await fetch(path, {
      ...opts,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(opts.headers || {}) },
    });
    const payload = await res.json();
    if (!res.ok) {
      const error = new Error(payload.error || res.statusText);
      error.payload = payload;
      throw error;
    }
    return payload;
  }, [token]);

  useEffect(() => {
    if (!token) {
      setAuthStatus('unauthenticated');
      setAuthError('Entre com uma conta de administrador para continuar.');
      return;
    }
    let active = true;
    fetch('/api/auth/session', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async response => {
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Sessão inválida');
        if (data.user?.role !== 'admin') throw new Error('Esta conta não possui acesso administrativo.');
        if (active) setAuthStatus('authenticated');
      })
      .catch(error => {
        if (!active) return;
        setAuthError(error.message || 'Não foi possível confirmar o acesso.');
        setAuthStatus('unauthenticated');
      });
    return () => { active = false; };
  }, [token]);

  const loadClientes = useCallback(async () => {
    if (authStatus !== 'authenticated') return;
    setLoadingClientes(true);
    setErrorClientes('');
    try {
      const data = await apiFetch('/admin/clientes');
      setClientes(data);
    } catch (err) {
      setErrorClientes(err.message);
    } finally {
      setLoadingClientes(false);
    }
  }, [apiFetch, authStatus]);

  const loadUsuarios = useCallback(async () => {
    if (authStatus !== 'authenticated') return;
    setLoadingUsuarios(true);
    setErrorUsuarios('');
    try {
      const data = await apiFetch('/admin/usuarios?maxId=100');
      setUsuarios(data);
    } catch (err) {
      setErrorUsuarios(err.message);
    } finally {
      setLoadingUsuarios(false);
    }
  }, [apiFetch, authStatus]);

  const loadLegacyPositions = useCallback(async () => {
    if (authStatus !== 'authenticated') return;
    setLoadingLegacy(true);
    setErrorLegacy('');
    try {
      const data = await apiFetch('/admin/portfolio/legacy');
      setLegacyPositions(data);
    } catch (err) {
      setErrorLegacy(err.message);
    } finally {
      setLoadingLegacy(false);
    }
  }, [apiFetch, authStatus]);

  const loadLocalUsers = useCallback(async () => {
    if (authStatus !== 'authenticated') return;
    try {
      const data = await apiFetch('/admin/local-users');
      setLocalUsers(data);
    } catch (err) {
      setErrorLegacy(err.message);
    }
  }, [apiFetch, authStatus]);

  const loadRecoveryFailures = useCallback(async () => {
    if (authStatus !== 'authenticated') return;
    setLoadingRecoveryFailures(true);
    setErrorRecoveryFailures('');
    try {
      const data = await apiFetch('/admin/password-recovery-failures?limit=50');
      setRecoveryFailures(Array.isArray(data) ? data : []);
    } catch (err) {
      setErrorRecoveryFailures(err.message);
    } finally {
      setLoadingRecoveryFailures(false);
    }
  }, [apiFetch, authStatus]);

  const loadWhatsAppStatus = useCallback(async () => {
    if (authStatus !== 'authenticated') return;
    try {
      const data = await apiFetch('/api/admin/whatsapp/status');
      setWhatsappStatus(data.status || 'falta token');
      setWhatsappOrigin(data.origem || data.origin || null);
      setWhatsappExpiresAt(data.expiresAt || null);
    } catch (_) {
      // fallback gracioso se rota não responder
    }
  }, [apiFetch, authStatus]);

  const saveWhatsAppTokenHandler = async () => {
    if (!whatsappInputToken.trim()) return;
    setSavingWhatsappToken(true);
    setWhatsappNotice('');
    setWhatsappTestResult(null);
    try {
      const res = await apiFetch('/api/admin/whatsapp/token', {
        method: 'POST',
        body: JSON.stringify({ token: whatsappInputToken.trim() }),
      });
      setWhatsappInputToken('');
      setWhatsappStatus(res.status || 'configurado');
      setWhatsappOrigin(res.origem || res.origin || 'banco');
      setWhatsappNotice(res.message || 'Token permanente salvo e cifrado com sucesso.');
    } catch (err) {
      setWhatsappNotice(err.message || 'Falha ao salvar token cifrado.');
    } finally {
      setSavingWhatsappToken(false);
    }
  };

  const testWhatsAppChannelHandler = async () => {
    setTestingWhatsapp(true);
    setWhatsappTestResult(null);
    try {
      const res = await apiFetch('/api/admin/whatsapp/test', { method: 'POST' });
      setWhatsappTestResult(res);
      if (res.status) setWhatsappStatus(res.status);
      if (res.origem || res.origin) setWhatsappOrigin(res.origem || res.origin);
    } catch (err) {
      setWhatsappTestResult({
        ok: false,
        error: err.message || 'Falha ao validar canal na Meta.',
      });
    } finally {
      setTestingWhatsapp(false);
    }
  };

  const loadJobOperations = useCallback(async () => {
    if (authStatus !== 'authenticated') return;
    setLoadingJobHistory(true);
    setJobHistoryError('');
    try {
      const [overview, history] = await Promise.all([
        apiFetch('/api/admin/jobs-overview'),
        apiFetch('/api/admin/job-runs?limit=30'),
      ]);
      setJobOverview(overview);
      setJobHistory(Array.isArray(history) ? history : []);
    } catch (error) {
      setJobHistoryError(error.message);
    } finally {
      setLoadingJobHistory(false);
    }
  }, [apiFetch, authStatus]);

  useEffect(() => {
    if (authStatus !== 'authenticated') return;
    loadClientes();
    loadUsuarios();
    loadLegacyPositions();
    loadLocalUsers();
    loadRecoveryFailures();
    loadJobOperations();
    loadWhatsAppStatus();
  }, [authStatus, token, loadClientes, loadUsuarios, loadLegacyPositions, loadLocalUsers, loadRecoveryFailures, loadJobOperations, loadWhatsAppStatus]);

  useEffect(() => {
    if (!activeJob) return undefined;
    let cancelled = false;

    const poll = async () => {
      try {
        const result = await apiFetch(`/api/admin/jobs/${activeJob.jobId}`, {
          headers: { 'X-Admin-Provision-Password': activeJob.secret },
        });
        if (cancelled) return;
        setJobResults(previous => ({ ...previous, [activeJob.name]: result }));
        if (['succeeded', 'failed'].includes(result.status)) {
          setJobRunning('');
          setActiveJob(null);
          loadJobOperations();
        }
      } catch (error) {
        if (cancelled) return;
        setJobResults(previous => ({
          ...previous,
          [activeJob.name]: { ...previous[activeJob.name], error: error.message },
        }));
      }
    };

    poll();
    const timer = setInterval(poll, 5000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [activeJob, apiFetch, loadJobOperations]);

  const handleStatus = async (id, status) => {
    try {
      const updated = await apiFetch(`/admin/clientes/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });
      setClientes(prev => prev.map(c => c.id === id ? updated : c));
    } catch (err) {
      alert('Erro: ' + err.message);
    }
  };

  const handleLegacyAssignment = async (positionId) => {
    const ownerId = Number(legacyOwners[positionId]);
    if (!ownerId) return;
    try {
      await apiFetch(`/admin/portfolio/legacy/${positionId}/assign`, {
        method: 'PATCH',
        body: JSON.stringify({ user_id: ownerId }),
      });
      setLegacyPositions(previous => previous.filter(position => position.id !== positionId));
      setLegacyOwners(previous => {
        const next = { ...previous };
        delete next[positionId];
        return next;
      });
    } catch (err) {
      setErrorLegacy(err.message);
    }
  };

  const copyText = (text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(''), 1800);
    });
  };

  const runDataJob = async (jobName) => {
    if (!jobSecret) {
      setJobResults(previous => ({
        ...previous,
        [jobName]: { ok: false, error: 'Informe a senha de provisionamento para executar o job.' },
      }));
      return;
    }
    if (jobName === 'all' && !window.confirm(
      'Executar todos os jobs no banco de produção? A operação pode levar vários minutos.',
    )) return;

    setJobRunning(jobName);
    setJobResults(previous => ({
      ...previous,
      [jobName]: { running: true },
    }));
    try {
      const result = await apiFetch(`/api/admin/jobs/${jobName}`, {
        method: 'POST',
        headers: { 'X-Admin-Provision-Password': jobSecret },
      });
      setJobResults(previous => ({
        ...previous,
        [jobName]: { ...result, running: true, progress: 0 },
      }));
      setActiveJob({ name: jobName, jobId: result.jobId, secret: jobSecret });
    } catch (error) {
      setJobResults(previous => ({
        ...previous,
        [jobName]: error.payload?.steps
          ? error.payload
          : { ok: false, error: error.message },
      }));
      setJobRunning('');
    }
  };

  const webhookUrl = 'https://fatcat.com.br/webhook/kiwify?token=SEU_SEGREDO';

  const stats = {
    total:        clientes.length,
    ativos:       clientes.filter(c => c.status === 'ativo').length,
    cancelados:   clientes.filter(c => c.status === 'cancelado').length,
    inadimplentes: clientes.filter(c => c.status === 'inadimplente').length,
    mrr:          clientes.filter(c => c.status === 'ativo').reduce((s, c) => s + (c.valor_pago || 0), 0),
  };

  const filtered = clientes.filter(c => {
    const q = search.toLowerCase();
    const matchSearch = !q || c.nome?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q) || c.whatsapp?.includes(q);
    const matchStatus = filterStatus === 'todos' || c.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const filteredUsuarios = usuarios.filter(u => {
    const q = searchUsuarios.toLowerCase();
    return !q || u.username?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
  });

  // ── CHECKING ───────────────────────────────────────────────────────────────
  if (authStatus === 'checking') {
    return (
      <div className="ap-login-root">
        <div className="ap-login-card" style={{ textAlign: 'center', color: '#8fa5c5' }}>
          <div className="ap-login-logo"><img src="/fatcat-logo-full.png" alt="FatCat" /></div>
          <div style={{ marginTop: 16, fontSize: 14 }}>Verificando acesso...</div>
        </div>
      </div>
    );
  }

  // ── LOGIN ──────────────────────────────────────────────────────────────────
  if (authStatus === 'unauthenticated') {
    return (
      <div className="ap-login-root">
        <div className="ap-login-card">
          <div className="ap-login-logo">
            <img src="/fatcat-logo-full.png" alt="FatCat" />
          </div>
          <div className="ap-login-title">Acesso administrativo</div>
          <div className="ap-login-error">{authError || 'Esta área é exclusiva para contas de administrador.'}</div>
          {onExit && (
            <button className="ap-login-back" onClick={onExit}>← Voltar para o início</button>
          )}
        </div>
      </div>
    );
  }

  // ── DASHBOARD ──────────────────────────────────────────────────────────────
  return (
    <div className="ap-root">
      <header className="ap-header">
        <div className="ap-header-left">
          <img src="/fatcat-logo-full.png" alt="FatCat" className="ap-header-logo" />
          <span className="ap-header-badge">Admin</span>
        </div>
        <div className="ap-header-right">
          <button className="ap-btn-ghost" onClick={() => { loadClientes(); loadUsuarios(); loadLocalUsers(); loadLegacyPositions(); loadRecoveryFailures(); loadJobOperations(); loadWhatsAppStatus(); }} title="Atualizar">
            ↻ Atualizar
          </button>
          {onExit && <button className="ap-btn-ghost" onClick={onExit}>← Dashboard</button>}
        </div>
      </header>

      <main className="ap-main">

        {/* Tabs */}
        <div className="ap-tabs">
          <button
            className={`ap-tab ${activeTab === 'usuarios' ? 'ap-tab--active' : ''}`}
            onClick={() => setActiveTab('usuarios')}
          >
            Usuários da Plataforma
            <span className="ap-tab-count">{Array.isArray(localUsers) ? localUsers.length : 0}</span>
          </button>
          <button
            className={`ap-tab ${activeTab === 'kiwify' ? 'ap-tab--active' : ''}`}
            onClick={() => setActiveTab('kiwify')}
          >
            Assinantes Kiwify
            <span className="ap-tab-count">{clientes.length}</span>
          </button>
          <button
            className={`ap-tab ${activeTab === 'legacyPortfolio' ? 'ap-tab--active' : ''}`}
            onClick={() => setActiveTab('legacyPortfolio')}
          >
            Carteira histórica
            <span className="ap-tab-count">{legacyPositions.length}</span>
          </button>
          <button
            className={`ap-tab ${activeTab === 'passwordRecovery' ? 'ap-tab--active' : ''}`}
            onClick={() => setActiveTab('passwordRecovery')}
          >
            Recuperação de senha
            <span className={`ap-tab-count ${recoveryFailures.length ? 'ap-tab-count--alert' : ''}`}>
              {recoveryFailures.length}
            </span>
          </button>
        </div>

        {/* ─── PAINEL CANAL WHATSAPP ─────────────────────────────────── */}
        <section className="ap-whatsapp-panel" data-testid="whatsapp-channel-panel">
          <div className="ap-jobs-heading">
            <div>
              <div className="ap-section-title">Canal WhatsApp Business</div>
              <div className="ap-section-subtitle">
                Status de entrega de alertas, token permanente da Cloud API e validação
              </div>
            </div>
            <div className="ap-whatsapp-status-badge-wrap">
              <span
                data-testid="whatsapp-status-badge"
                className={`ap-job-status ${
                  whatsappStatus === 'configurado'
                    ? 'ap-job-status--succeeded'
                    : whatsappStatus && whatsappStatus.startsWith('expira em')
                      ? 'ap-job-status--pending'
                      : 'ap-job-status--failed'
                }`}
              >
                {whatsappStatus || 'falta token'}
              </span>
              {whatsappOrigin && (
                <span data-testid="whatsapp-origin" className="ap-whatsapp-origin">
                  Origem: {whatsappOrigin === 'banco' ? 'Banco de dados' : 'Variável do servidor'}
                </span>
              )}
            </div>
          </div>

          <div className="ap-whatsapp-content">
            <div className="ap-whatsapp-form">
              <label className="ap-whatsapp-label">
                <span>Token permanente (Meta Cloud API)</span>
                <div className="ap-whatsapp-input-row">
                  <input
                    data-testid="input-whatsapp-token"
                    type="password"
                    value={whatsappInputToken}
                    onChange={e => setWhatsappInputToken(e.target.value)}
                    placeholder={whatsappStatus === 'configurado' || (whatsappStatus && whatsappStatus.startsWith('expira em'))
                      ? '●●●●●●●●●●●● (configurado)'
                      : 'Informe o token permanente da Meta'}
                    autoComplete="off"
                  />
                  <button
                    data-testid="btn-save-whatsapp-token"
                    type="button"
                    className="ap-btn-job"
                    onClick={saveWhatsAppTokenHandler}
                    disabled={savingWhatsappToken || !whatsappInputToken.trim()}
                  >
                    {savingWhatsappToken ? 'Salvando…' : 'Salvar token'}
                  </button>
                  <button
                    data-testid="btn-test-whatsapp"
                    type="button"
                    className="ap-btn-ghost"
                    onClick={testWhatsAppChannelHandler}
                    disabled={testingWhatsapp}
                  >
                    {testingWhatsapp ? 'Testando…' : 'Testar'}
                  </button>
                </div>
              </label>
              <small className="ap-whatsapp-hint">
                O token é salvo cifrado no banco com chave do servidor (ENCRYPTION_KEY). Nunca é exibido em texto puro nem gravado em logs.
              </small>
            </div>

            {whatsappNotice && (
              <div data-testid="whatsapp-notice" className="ap-whatsapp-notice">
                {whatsappNotice}
              </div>
            )}

            {whatsappTestResult && (
              <div
                data-testid="whatsapp-test-result"
                className={`ap-job-result ${whatsappTestResult.ok ? '' : 'ap-job-result--error'}`}
                style={{ marginTop: '12px' }}
              >
                <div className="ap-job-result-header">
                  <strong>Resultado do teste</strong>
                  <span>{whatsappTestResult.ok ? 'Sucesso' : 'Falha'}</span>
                </div>
                {whatsappTestResult.message && <div>{whatsappTestResult.message}</div>}
                {whatsappTestResult.error && <div className="ap-job-error">{whatsappTestResult.error}</div>}
              </div>
            )}
          </div>
        </section>

        <section className="ap-jobs-panel">
          <div className="ap-jobs-heading">
            <div>
              <div className="ap-section-title">Jobs de dados em produção</div>
              <div className="ap-section-subtitle">
                Executados dentro da VM publicada, usando o PostgreSQL de produção.
              </div>
            </div>
            <label className="ap-job-secret">
              <span>Senha de provisionamento</span>
              <input
                type="password"
                value={jobSecret}
                onChange={event => setJobSecret(event.target.value)}
                placeholder="Informe o Secret"
                autoComplete="off"
              />
            </label>
          </div>
          {jobOverview?.migracoes && (
            <div
              className={`ap-migrations-banner ${jobOverview.migracoes.total === 0 ? 'ap-migrations-banner--ok' : 'ap-migrations-banner--pending'}`}
              data-testid="admin-migrations-panel"
            >
              <div className="ap-migrations-header">
                <div>
                  <div className="ap-migrations-title">
                    {jobOverview.migracoes.total > 0
                      ? `Migrations pendentes em produção (${jobOverview.migracoes.total})`
                      : 'Migrations em produção: banco atualizado'}
                  </div>
                  <div className="ap-migrations-subtitle">
                    {jobOverview.migracoes.total > 0
                      ? 'Comparação entre database/migrations e registro aplicado. Jobs bloqueados pulam com aviso claro.'
                      : 'Todas as migrations de database/migrations foram aplicadas ou conciliadas.'}
                  </div>
                </div>
                <span
                  className={`ap-job-status ${jobOverview.migracoes.total > 0 ? 'ap-job-status--blocked' : 'ap-job-status--succeeded'}`}
                  data-testid="admin-migrations-badge"
                >
                  {jobOverview.migracoes.total > 0 ? `${jobOverview.migracoes.total} pendente(s)` : 'em dia'}
                </span>
              </div>
              {jobOverview.migracoes.total > 0 && (
                <>
                  <ul className="ap-migrations-list" data-testid="admin-pending-migrations-list">
                    {(jobOverview.migracoes.detalhes || jobOverview.migracoes.pendentes.map(f => ({ filename: f, jobsBloqueados: [] }))).map(item => (
                      <li key={item.filename} className="ap-migrations-item" data-testid={`migration-item-${item.filename}`}>
                        <code className="ap-migrations-filename">{item.filename}</code>
                        {item.jobsBloqueados && item.jobsBloqueados.length > 0 ? (
                          <span className="ap-migrations-blocked-jobs" data-testid={`migration-blocked-${item.filename}`}>
                            {' '}— bloqueia: <strong>{item.jobsBloqueados.map(j => JOB_LABELS[j] || j).join(', ')}</strong>
                          </span>
                        ) : (
                          <span className="ap-migrations-blocked-jobs"> — nenhum job bloqueado</span>
                        )}
                      </li>
                    ))}
                  </ul>
                  <div className="ap-migrations-hint" data-testid="admin-migrations-reconciliation-hint">
                    Para conciliar migrations cujas tabelas já existem no banco: <code className="ap-migrations-cmd">npm run migrate -- --conciliar --simular</code>
                  </div>
                </>
              )}
            </div>
          )}
          {jobOverview && (
            <>
              <div className="ap-schedule-grid">
                {jobOverview.schedule.map(item => (
                  <article className="ap-schedule-card" key={item.key}>
                    <div className="ap-schedule-card-head">
                      <strong>{item.name}</strong>
                      <span className={`ap-job-status ap-job-status--${item.atrasado ? 'failed' : (item.bloqueadoPorMigration ? (item.latest?.status === 'skipped' ? 'skipped' : 'blocked') : (item.latest?.status === 'skipped' ? 'skipped' : (item.latest?.status || 'pending')))}`}>
                        {item.atrasado
                          ? 'atrasado'
                          : item.bloqueadoPorMigration
                            ? (item.latest?.status === 'skipped' ? 'pulado (migration pendente)' : 'bloqueado')
                            : (item.latest?.status === 'skipped' ? 'pulado' : (item.latest?.status || 'sem execução'))}
                      </span>
                    </div>
                    <p>{item.cadence}</p>
                    <small>{item.window}</small>
                    {item.bloqueadoPorMigration && (
                      <div className="ap-job-blocked-notice" data-testid={`job-blocked-${item.key}`}>
                        Migration {item.bloqueadoPorMigration} pendente
                      </div>
                    )}
                    {item.atrasado && item.motivoAtraso && (
                      <div className="ap-job-error">{item.motivoAtraso}</div>
                    )}
                    {item.latest?.status === 'failed' && item.latest?.error && (
                      <div className="ap-job-error">{item.latest.error}</div>
                    )}
                    {item.latest?.status === 'skipped' && item.latest?.log && (
                      <div className="ap-job-warning" data-testid={`job-skipped-${item.key}`}>{item.latest.log}</div>
                    )}
                    <div className="ap-schedule-last">
                      Último resultado: {item.latest?.finishedAt
                        ? fmtDateTime(item.latest.finishedAt)
                        : item.latest?.startedAt
                          ? fmtDateTime(item.latest.startedAt)
                          : 'ainda não registrado'}
                    </div>
                  </article>
                ))}
              </div>
              <div className="ap-brapi-budget">
                <div>
                  <span>BRAPI estimada por dia de pregão</span>
                  <strong>{Number(jobOverview.budget.total || 0).toLocaleString('pt-BR')} chamadas</strong>
                </div>
                <p>
                  Cotações globais: {Number(jobOverview.budget.quoteFullCalls + jobOverview.budget.quoteHotCalls).toLocaleString('pt-BR')}
                  {' '}· coleta diária: {Number(jobOverview.budget.dailyCollectCalls).toLocaleString('pt-BR')}
                  {' '}· scanner intraday: {Number(jobOverview.budget.intradayCalls).toLocaleString('pt-BR')}
                </p>
              </div>
            </>
          )}
          <div className="ap-jobs-actions">
            {DATA_JOBS.map(job => (
              <button
                key={job.name}
                type="button"
                className={`ap-btn-job${job.emphasis ? ' ap-btn-job--emphasis' : ''}`}
                onClick={() => runDataJob(job.name)}
                disabled={Boolean(jobRunning)}
              >
                {jobRunning === job.name ? 'Executando…' : job.label}
              </button>
            ))}
          </div>
          {Object.entries(jobResults).map(([jobName, result]) => (
            <div
              key={jobName}
              className={`ap-job-result ${result.status === 'failed' || result.ok === false ? 'ap-job-result--error' : ''}`}
            >
              <div className="ap-job-result-header">
                <strong>{DATA_JOBS.find(job => job.name === jobName)?.label || jobName}</strong>
                <span>
                  {result.status === 'succeeded'
                    ? 'Concluído'
                    : result.status === 'failed'
                      ? 'Falhou'
                      : result.running || ['queued', 'running'].includes(result.status)
                        ? 'Em execução…'
                        : result.ok === false ? 'Falhou' : 'Concluído'}
                </span>
              </div>
              {result.error && <div className="ap-job-error">{result.error}</div>}
              {result.jobId && (
                <div className="ap-job-progress">
                  <div className="ap-job-progress-meta">
                    <span>{result.stage || result.requested}</span>
                    <span>{Math.max(0, Math.min(100, Number(result.progress || 0))).toFixed(1)}%</span>
                  </div>
                  <div className="ap-job-progress-track">
                    <div
                      className="ap-job-progress-fill"
                      style={{ width: `${Math.max(0, Math.min(100, Number(result.progress || 0)))}%` }}
                    />
                  </div>
                  <div className="ap-job-progress-detail">
                    {result.ticker && <span>Ativo: {result.ticker}</span>}
                    <span>Processadas: {Number(result.rowsProcessed || 0).toLocaleString('pt-BR')}</span>
                    <span>Inseridas/atualizadas: {Number(result.rowsInserted || 0).toLocaleString('pt-BR')}</span>
                  </div>
                </div>
              )}
              {result.log && <pre>{result.log}</pre>}
            </div>
          ))}
          <div className="ap-job-history-heading">
            <div>
              <strong>Últimas execuções</strong>
              <span>Retenção de {jobOverview?.retentionDays || 90} dias; falhas aparecem primeiro.</span>
            </div>
            <button type="button" className="ap-btn-ghost" onClick={loadJobOperations} disabled={loadingJobHistory}>
              {loadingJobHistory ? 'Atualizando…' : '↻ Atualizar histórico'}
            </button>
          </div>
          {jobHistoryError && <div className="ap-job-error">{jobHistoryError}</div>}
          <div className="ap-job-history">
            {jobHistory.map(run => (
              <article
                key={run.jobId}
                className={`ap-job-history-row${run.status === 'failed' ? ' ap-job-history-row--failed' : ''}`}
              >
                <div className="ap-job-history-main">
                  <strong>{JOB_LABELS[run.requested] || run.requested}</strong>
                  <span className={`ap-job-status ap-job-status--${run.status}`}>{run.status}</span>
                  <time>{fmtDateTime(run.finishedAt || run.startedAt)}</time>
                </div>
                <div className="ap-job-history-metrics">
                  <span>Itens: {Number(run.itemsProcessed || run.rowsProcessed || 0).toLocaleString('pt-BR')}</span>
                  <span>BRAPI: {Number(run.brapiCalls || 0).toLocaleString('pt-BR')}</span>
                  {run.metadata?.silentCycles && <span>Ciclos silenciosos: {run.metadata.silentCycles}</span>}
                </div>
                {run.error && <div className="ap-job-error">{run.error}</div>}
                {run.log && (
                  <details className="ap-job-history-log">
                    <summary>Ver log</summary>
                    <pre>{run.log}</pre>
                  </details>
                )}
              </article>
            ))}
            {!loadingJobHistory && jobHistory.length === 0 && (
              <div className="ap-job-history-empty">Nenhuma execução registrada nos últimos 90 dias.</div>
            )}
          </div>
        </section>

        {/* ─── TAB: USUÁRIOS DA PLATAFORMA ─────────────────────────────────── */}
        {activeTab === 'usuarios' && (
          <>
            <div className="ap-toolbar">
              <input
                className="ap-search"
                type="text"
                placeholder="Buscar por nome ou e-mail..."
                value={searchUsuarios}
                onChange={e => setSearchUsuarios(e.target.value)}
              />
              <span className="ap-toolbar-hint">
                {usuarios.length} usuário(s) cadastrado(s) na plataforma
              </span>
            </div>

            {errorUsuarios && <div className="ap-error">Erro: {errorUsuarios}</div>}

            {loadingUsuarios ? (
              <div className="ap-loading">Buscando usuários na plataforma...</div>
            ) : (
              <div className="ap-table-wrap">
                <table className="ap-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Nome / E-mail</th>
                      <th>Plano / Validade</th>
                      <th>Créditos</th>
                      <th>Ativo</th>
                      <th>Último Acesso</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsuarios.length === 0 && (
                      <tr><td colSpan={6} className="ap-empty">
                        {loadingUsuarios ? 'Carregando...' : 'Nenhum usuário encontrado.'}
                      </td></tr>
                    )}
                    {filteredUsuarios.map(u => (
                      <tr key={u.id}>
                        <td className="ap-cell-mono" style={{ color: '#5b9dff' }}>#{u.id}</td>
                        <td>
                          <div className="ap-cell-nome">{u.username || '—'}</div>
                          <div className="ap-cell-email">{u.email}</div>
                        </td>
                        <td>
                          <div style={{ fontSize: 12 }}>
                            {u.renewDate ? `Até ${fmt(u.renewDate)}` : '—'}
                          </div>
                        </td>
                        <td className="ap-cell-mono">{u.credits ?? '—'}</td>
                        <td>
                          <span className={`ap-badge ${u.ativo ? 'ap-badge--ativo' : 'ap-badge--inativo'}`}>
                            {u.ativo ? 'Sim' : 'Não'}
                          </span>
                        </td>
                        <td className="ap-cell-mono">
                          {u.last_access
                            ? fmtDateTime(u.last_access)
                            : <span style={{ color: '#4a5a75', fontSize: 11 }}>Nunca registrado</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="ap-footer">
              {filteredUsuarios.length} de {usuarios.length} usuário(s) · FatCat Admin
            </div>
          </>
        )}

        {activeTab === 'legacyPortfolio' && (
          <>
            <div className="ap-toolbar">
              <span className="ap-toolbar-hint">
                Posições antigas sem dono só aparecem aqui até que você as atribua à conta correta.
              </span>
            </div>
            {errorLegacy && <div className="ap-error">Erro: {errorLegacy}</div>}
            {loadingLegacy ? (
              <div className="ap-loading">Buscando posições históricas...</div>
            ) : (
              <div className="ap-table-wrap">
                <table className="ap-table">
                  <thead>
                    <tr><th>Ativo</th><th>Estratégia</th><th>Entrada</th><th>Qtd.</th><th>Dono correto</th><th /></tr>
                  </thead>
                  <tbody>
                    {legacyPositions.length === 0 && (
                      <tr><td colSpan={6} className="ap-empty">Nenhuma posição histórica pendente de atribuição.</td></tr>
                    )}
                    {legacyPositions.map(position => (
                      <tr key={position.id}>
                        <td>
                          <div className="ap-cell-nome">{position.ticker}</div>
                          <div className="ap-cell-email">{position.name || '—'}</div>
                        </td>
                        <td>{position.strategy || 'Sem estratégia'}</td>
                        <td className="ap-cell-mono">R$ {Number(position.entry_price).toFixed(2).replace('.', ',')}</td>
                        <td className="ap-cell-mono">{position.quantity}</td>
                        <td>
                          <select
                            className="ap-select"
                            value={legacyOwners[position.id] || ''}
                            onChange={event => setLegacyOwners(previous => ({ ...previous, [position.id]: event.target.value }))}
                          >
                            <option value="">Selecione uma conta</option>
                            {localUsers.map(user => (
                              <option key={user.id} value={user.id}>
                                #{user.id} · {user.username || user.email}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <button
                            className="ap-btn-status ap-btn-status--ativo"
                            disabled={!legacyOwners[position.id]}
                            onClick={() => handleLegacyAssignment(position.id)}
                          >
                            Atribuir
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {activeTab === 'passwordRecovery' && (
          <>
            <div className="ap-recovery-notice">
              <div className="ap-recovery-notice-title">
                <span className="ap-recovery-indicator" />
                Falhas de entrega que precisam de atenção
              </div>
              <div className="ap-recovery-notice-text">
                A confirmação pública continua neutra. Esta lista mostra apenas o e-mail,
                o resumo seguro do erro e o horário da falha — nunca tokens ou senhas.
              </div>
            </div>

            {errorRecoveryFailures && <div className="ap-error">Erro: {errorRecoveryFailures}</div>}

            {loadingRecoveryFailures ? (
              <div className="ap-loading">Buscando falhas de recuperação...</div>
            ) : (
              <div className="ap-table-wrap">
                <table className="ap-table">
                  <thead>
                    <tr><th>E-mail</th><th>Resumo do erro</th><th>Registrada em</th></tr>
                  </thead>
                  <tbody>
                    {recoveryFailures.length === 0 && (
                      <tr><td colSpan={3} className="ap-empty">Nenhuma falha de entrega registrada.</td></tr>
                    )}
                    {recoveryFailures.map(failure => (
                      <tr key={failure.id}>
                        <td>
                          <div className="ap-cell-email ap-cell-email--visible">{failure.email}</div>
                        </td>
                        <td className="ap-recovery-error">{failure.error_summary || 'Falha não especificada'}</td>
                        <td className="ap-cell-mono">{fmtDateTime(failure.failed_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="ap-footer">
              {recoveryFailures.length} falha(s) recente(s) · FatCat Admin
            </div>
          </>
        )}

        {/* ─── TAB: KIWIFY ASSINANTES ──────────────────────────────────────── */}
        {activeTab === 'kiwify' && (
          <>
            <div className="ap-stats">
              <div className="ap-stat-card">
                <span className="ap-stat-val">{stats.total}</span>
                <span className="ap-stat-lbl">Total</span>
              </div>
              <div className="ap-stat-card ap-stat-card--green">
                <span className="ap-stat-val">{stats.ativos}</span>
                <span className="ap-stat-lbl">Ativos</span>
              </div>
              <div className="ap-stat-card ap-stat-card--red">
                <span className="ap-stat-val">{stats.cancelados + stats.inadimplentes}</span>
                <span className="ap-stat-lbl">Cancelados / Inad.</span>
              </div>
              <div className="ap-stat-card ap-stat-card--blue">
                <span className="ap-stat-val">{fmtBRL(stats.mrr)}</span>
                <span className="ap-stat-lbl">Receita (ativos)</span>
              </div>
            </div>

            <div className="ap-webhook-bar">
              <span className="ap-webhook-label">🔗 Webhook Kiwify:</span>
              <code className="ap-webhook-url">{webhookUrl}</code>
              <button className="ap-btn-copy" onClick={() => copyText(webhookUrl, 'wh')}>
                {copied === 'wh' ? '✓ Copiado' : 'Copiar'}
              </button>
              <span className="ap-webhook-hint">
                Troque SEU_SEGREDO pelo valor de KIWIFY_TOKEN antes de salvar na Kiwify.
                O segredo só é aceito na URL; não o envie no corpo ou em cabeçalhos.
                Se a entrega usar assinatura digital, configure também a chave pública ativa em
                {' '}KIWIFY_WEBHOOK_PUBLIC_KEY.
              </span>
            </div>

            <div className="ap-toolbar">
              <input
                className="ap-search"
                type="text"
                placeholder="Buscar por nome, e-mail ou WhatsApp..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              <div className="ap-filter-tabs">
                {['todos', 'ativo', 'cancelado', 'inadimplente', 'inativo'].map(s => (
                  <button
                    key={s}
                    className={`ap-filter-tab ${filterStatus === s ? 'ap-filter-tab--active' : ''}`}
                    onClick={() => setFilterStatus(s)}
                  >
                    {s === 'todos' ? 'Todos' : STATUS_LABELS[s]?.label || s}
                    {s === 'todos' ? ` (${clientes.length})` : ` (${clientes.filter(c => c.status === s).length})`}
                  </button>
                ))}
              </div>
            </div>

            {errorClientes && <div className="ap-error">Erro: {errorClientes}</div>}

            {loadingClientes ? (
              <div className="ap-loading">Carregando clientes...</div>
            ) : (
              <div className="ap-table-wrap">
                <table className="ap-table">
                  <thead>
                    <tr>
                      <th>Nome / E-mail</th>
                      <th>WhatsApp</th>
                      <th>Status</th>
                      <th>Plano</th>
                      <th>Pagamento</th>
                      <th>Expiração</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 && (
                      <tr><td colSpan={7} className="ap-empty">
                        {clientes.length === 0
                          ? 'Nenhum assinante ainda. Configure o webhook Kiwify acima para receber novos assinantes automaticamente.'
                          : 'Nenhum cliente encontrado com esses filtros.'}
                      </td></tr>
                    )}
                    {filtered.map(c => (
                      <tr key={c.id} className={c.status !== 'ativo' ? 'ap-row--dim' : ''}>
                        <td>
                          <div className="ap-cell-nome">{c.nome || '—'}</div>
                          <div className="ap-cell-email">{c.email}</div>
                        </td>
                        <td className="ap-cell-mono">{c.whatsapp || '—'}</td>
                        <td>
                          <span className={`ap-badge ${STATUS_LABELS[c.status]?.cls || ''}`}>
                            {STATUS_LABELS[c.status]?.label || c.status}
                          </span>
                        </td>
                        <td>{c.plano || '—'}</td>
                        <td className="ap-cell-mono">{fmt(c.data_pagamento)}</td>
                        <td className="ap-cell-mono">{fmt(c.data_expiracao)}</td>
                        <td>
                          <div className="ap-actions">
                            <select
                              className="ap-select"
                              value={c.status}
                              onChange={e => handleStatus(c.id, e.target.value)}
                            >
                              <option value="ativo">Ativo</option>
                              <option value="inativo">Inativo</option>
                              <option value="cancelado">Cancelado</option>
                              <option value="inadimplente">Inadimplente</option>
                            </select>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="ap-footer">
              {filtered.length} de {clientes.length} assinante(s) · FatCat Admin
            </div>
          </>
        )}

      </main>
    </div>
  );
}
